import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ErrorCode } from '@trulyinbox/errors';
import { ApiError, consumeSessionSupersededToast, isSessionSupersededError } from './api';
import { notifyApiError, notifySuccess, notifyWarning } from './toast';
import { useUpgradePromptStore } from './stores/upgrade-prompt';
import { PAUSED_TOAST_MESSAGE, isPausedErrorCode } from '@/features/billing/paused-copy';
import { billingKeys } from '@/features/billing/queries';

/**
 * Single QueryClient for the app.
 *
 * Defaults follow `frontend-principles` SKILL §Data Layer:
 *  - `staleTime: 30s` — lists/detail-ish data is fresh for 30s, then revalidated.
 *  - `gcTime: 5min` — keep recently-unused queries in cache for back-button speed.
 *  - `refetchOnWindowFocus: true` — pick up server-side changes when user comes back.
 *  - `retry`: skip retries on 401 (the fetch wrapper handles refresh itself),
 *    403/404 (a definitive access-denial or not-found — no retry changes the
 *    answer), and the cross-tab identity-swap 409 (`isSessionSupersededError`,
 *    see `lib/api.ts` — retrying just delays the one toast this needs),
 *    otherwise retry twice on transient errors.
 *
 * Global error pipe:
 *  - `QueryCache.onError` / `MutationCache.onError` route every unhandled error
 *    through `notifyApiError`, producing a single uniform toast for any
 *    request that bubbles up. Opt out at the callsite with
 *    `meta: { silentError: true }` when the caller owns its own inline error UI.
 *  - Query errors only auto-toast on background refetches — the initial load's
 *    error state is rendered inline by the consuming component, and toasting
 *    on first failure would double-fire alongside that.
 *  - Exception: the identity-swap 409 still follows the first-load rule on
 *    the query side (nearly every list/detail page renders a generic
 *    `isError` banner on a data-less first failure — toasting on top of that
 *    would show the same "reload this tab" sentence twice), but toasts
 *    unconditionally on the mutation side (mutations don't have an
 *    equivalent generic first-attempt banner). Either way it fires **at most
 *    once ever**, via `consumeSessionSupersededToast` — the underlying flag
 *    never resets, so every other active query/mutation and every later
 *    refetch would otherwise re-toast the same message forever. A callsite
 *    with its own generic inline-error fallback (e.g. `InviteByEmailCard`)
 *    must still recognise `isSessionSupersededError` itself and skip setting
 *    its own inline text — the global mutation toast above doesn't know
 *    about (and can't suppress) a component's local `onError`.
 *
 * Global success pipe (mirror of the error pipe):
 *  - `MutationCache.onSuccess` fires a single success toast for any mutation
 *    that opts in with `meta: { successMessage: '…' }`. This is the symmetric
 *    counterpart to `meta: { silentError }` — static, opt-in, fired centrally.
 *  - Mutations needing an interpolated message (a name, an email, a count)
 *    instead call `notifySuccess(...)` inline from their own `onSuccess`, and
 *    do NOT set `meta.successMessage`. The rule is xor: a mutation uses exactly
 *    one mechanism, so a success is never toasted twice.
 *
 * Per-query overrides:
 *  - Reference data: pass `staleTime: Infinity, gcTime: Infinity` at the query
 *    site (e.g. role lists, country lists).
 *  - Live/event-driven data: pass `staleTime: 0` and set up `invalidateQueries`
 *    on mutations.
 */
function isSilent(meta: Record<string, unknown> | undefined): boolean {
  return meta?.silentError === true;
}

function shouldSuppress(err: unknown): boolean {
  // 401 handling lives entirely in `lib/api.ts`; by the time we'd see one
  // here, the user is being redirected to /login.
  return err instanceof ApiError && err.status === 401;
}

/**
 * A write the plan doesn't grant (`040005`, thrown by the API's
 * `ResourceControlGuard`). The UI gates these entry points with
 * `<LockedFeature>`, so reaching here means the gate was bypassed — a tab left
 * open across a downgrade, or a control we haven't gated yet. Either way the
 * answer is "upgrade", not a red toast, so the prompt opens instead.
 *
 * Branch on the catalog constant, never on the message string.
 */
function isResourceAccessDenied(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.errorCode === ErrorCode.Billing.Shared.ResourceAccessDenied
  );
}

/**
 * The org's subscription is PAUSED and the backend refused a write.
 *
 * Branches on `errorCode` ONLY, never on message text, and goes through
 * `isPausedErrorCode` because the condition has two codes (`040006` general,
 * `020009` connect/reconnect) — see `features/billing/paused-copy.ts`.
 */
function isSubscriptionPausedError(err: unknown): boolean {
  return err instanceof ApiError && isPausedErrorCode(err.errorCode);
}

function successMessage(meta: Record<string, unknown> | undefined): string | null {
  const m = meta?.successMessage;
  return typeof m === 'string' && m.length > 0 ? m : null;
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err, query) => {
      // Fires for every active query at once, then again on every later
      // refetch (the underlying flag never resets) — surface it once, not
      // per-query and not per-refetch. Still respects the data-gate below:
      // a data-less first failure is already covered by the consuming
      // page's own generic `isError` banner (see e.g. EmailAccountsPage),
      // and some other already-loaded query in the tab will trip this same
      // toast anyway inside the 30s staleTime window.
      if (isSessionSupersededError(err)) {
        if (query.state.data !== undefined && consumeSessionSupersededToast()) {
          notifyApiError(err);
        }
        return;
      }
      if (isSilent(query.meta) || shouldSuppress(err)) return;
      // Avoid double-toasting the empty-state error component renders on the
      // first failed fetch — only fire when we already have data in the cache
      // (i.e. background refetch).
      if (query.state.data === undefined) return;
      notifyApiError(err);
    },
  }),
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => {
      if (isSessionSupersededError(err)) {
        if (consumeSessionSupersededToast()) notifyApiError(err);
        return;
      }
      // BEFORE the silent escape, unlike every other branch here. A paused
      // subscription changes what the whole app may do, and ~54 mutations set
      // `silentError` — leaving those out would let a tab that was open when
      // the pause landed keep rendering every control as enabled until the
      // summary's own staleness expired (up to ~105s). Refreshing the summary
      // + capabilities on the FIRST rejected write is what flips a stale tab
      // into read-only. It only invalidates; it shows nothing.
      if (isSubscriptionPausedError(err)) {
        void queryClient.invalidateQueries({ queryKey: billingKeys.summary() });
        void queryClient.invalidateQueries({ queryKey: billingKeys.capabilities() });
      }
      if (isSilent(mutation.meta) || shouldSuppress(err)) return;
      // Ordered after the silent/401 escapes on purpose: a mutation that owns its
      // own inline error UI still owns this one, and a 401 is already logging out.
      //
      // Paused is checked BEFORE `040005`: `ResourceControlGuard` now reports a
      // paused denial as `040006`, so the two can't collide — but the ordering
      // makes that independent of the guard. A warning toast, not the red error
      // one and not the upgrade prompt: the remedy is "resume", not "upgrade".
      if (isSubscriptionPausedError(err)) {
        notifyWarning(PAUSED_TOAST_MESSAGE);
        return;
      }
      if (isResourceAccessDenied(err)) {
        useUpgradePromptStore.getState().open();
        return;
      }
      notifyApiError(err);
    },
    onSuccess: (_data, _vars, _ctx, mutation) => {
      const msg = successMessage(mutation.meta);
      if (msg) notifySuccess(msg);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, err) => {
        if (
          err instanceof ApiError &&
          (err.status === 401 || err.status === 403 || err.status === 404)
        ) {
          return false;
        }
        if (isSessionSupersededError(err)) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
